/**
 * Staff content moderation endpoints.
 */
import { apiRequest, normalizeList } from "./staffUtils";
import { toDisplayString } from "../../utils/toDisplayString";

const CONTENT_TYPES = ["books", "courses", "journals", "learning-paths"];

export const staffContentInitialState = {
  staffContentItems: [],
  staffContentLoading: false,
  staffContentError: null,
  staffContentTotal: 0,
  staffContentPages: 1,
  staffContentDetail: null,
  staffContentDetailLoading: false,
  staffContentDetailError: null,
  staffContentMutationLoading: false,
  staffContentMutationError: null,
  // Pending review count (badge on the sidebar)
  staffPendingCount: 0,
};

export function createStaffContentActions(set, get) {
  async function mutate(type, id, action, method, body, successMessage) {
    if (!CONTENT_TYPES.includes(type)) {
      const error = `Unsupported content type: ${type}`;
      set({ staffContentMutationError: error });
      return { success: false, error };
    }
    set({ staffContentMutationLoading: true, staffContentMutationError: null });
    try {
      const data = await apiRequest(
        get,
        `/api/staff/content/${type}/${id}${action ? `/${action}` : ""}`,
        { method, body }
      );
      set({
        staffContentMutationLoading: false,
        staffContentMutationError: null,
        successMessage,
      });
      return { success: true, data: data?.data ?? data };
    } catch (err) {
      const error = toDisplayString(err.message, "Content action failed");
      set({ staffContentMutationLoading: false, staffContentMutationError: error });
      return { success: false, error };
    }
  }

  return {
    fetchStaffContent: async (type, params = {}) => {
      if (!CONTENT_TYPES.includes(type)) {
        const error = `Unsupported content type: ${type}`;
        set({ staffContentError: error });
        return { success: false, error };
      }
      set({ staffContentLoading: true, staffContentError: null });
      try {
        const query = new URLSearchParams(params).toString();
        const data = await apiRequest(
          get,
          `/api/staff/content/${type}${query ? `?${query}` : ""}`
        );
        const { items, total, totalPages } = normalizeList(data, [type, "items"]);
        set((state) => ({
          staffContentLoading: false,
          staffContentItems: [
            ...state.staffContentItems.filter((item) => item.contentType !== type),
            ...items.map((item) => ({ ...item, contentType: type })),
          ],
          staffContentTotal: state.staffContentTotal + total,
          staffContentPages: Math.max(state.staffContentPages, totalPages),
        }));
        return { success: true, items, total, totalPages };
      } catch (err) {
        const error = toDisplayString(err.message, "Failed to load content");
        set({ staffContentLoading: false, staffContentError: error });
        return { success: false, error };
      }
    },

    clearStaffContent: () =>
      set({
        staffContentItems: [],
        staffContentTotal: 0,
        staffContentPages: 1,
        staffContentError: null,
      }),

    // Sum of PENDING content across all types, used by the sidebar badge so the
    // staff member can see how many resources await review.
    fetchStaffPendingCount: async () => {
      try {
        const counts = await Promise.all(
          CONTENT_TYPES.map(async (type) => {
            const query = new URLSearchParams({
              status: "PENDING",
              page: "1",
              limit: "1",
            }).toString();
            const data = await apiRequest(
              get,
              `/api/staff/content/${type}${query ? `?${query}` : ""}`
            );
            const { total } = normalizeList(data, [type, "items"]);
            return total;
          })
        );
        const staffPendingCount = counts.reduce((sum, value) => sum + (value || 0), 0);
        set({ staffPendingCount });
        return { success: true, count: staffPendingCount };
      } catch (err) {
        return { success: false, error: toDisplayString(err.message, "Failed to load pending count") };
      }
    },

    fetchStaffContentDetail: async (type, id) => {
      if (!CONTENT_TYPES.includes(type)) {
        const error = `Unsupported content type: ${type}`;
        set({ staffContentDetailError: error });
        return { success: false, error };
      }
      set({ staffContentDetailLoading: true, staffContentDetailError: null });
      try {
        const response = await apiRequest(get, `/api/staff/content/${type}/${encodeURIComponent(id)}`);
        const detail =
          response?.data ??
          response?.book ??
          response?.course ??
          response?.journal ??
          response?.learningPath ??
          response;
        set({
          staffContentDetailLoading: false,
          staffContentDetail: { ...detail, contentType: type },
        });
        return { success: true, data: detail };
      } catch (err) {
        const error = toDisplayString(err.message, "Failed to load content detail");
        set({ staffContentDetailLoading: false, staffContentDetailError: error });
        return { success: false, error };
      }
    },

    updateStaffContent: (type, id, payload) =>
      mutate(type, id, "", "PATCH", payload, "Content updated successfully"),
    approveStaffContent: (type, id) =>
      mutate(type, id, "approve", "PATCH", {}, "Content approved successfully"),
    rejectStaffContent: (type, id, reason) =>
      mutate(type, id, "reject", "PATCH", { reason }, "Content rejected successfully"),
    disableStaffCourse: (id) =>
      mutate("courses", id, "disable", "PATCH", undefined, "Course disabled"),
    featureStaffCourse: (id) =>
      mutate("courses", id, "feature", "PATCH", undefined, "Course featured"),
    unfeatureStaffCourse: (id) =>
      mutate("courses", id, "unfeature", "PATCH", undefined, "Course unfeatured"),
  };
}

export { CONTENT_TYPES };
